import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, X, ArrowUpRight, Search, FileText, ExternalLink, Trash2, ChevronUp, ChevronDown, Loader2, Link2 } from "lucide-react";
import { WhatsAppIcon } from "../../components/WhatsAppIcon";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ShareDialog from "../../components/os/ShareDialog";
import ItemMenu from "../../components/os/ItemMenu";
import OwnerFilter from "../../components/os/OwnerFilter";
import { useTeamFilter } from "../../hooks/useTeamFilter";
import { api, formatApiError, mediaUrl } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";

const ease = [0.16, 1, 0.3, 1];
const FALLBACK_IMG = "https://images.unsplash.com/photo-1677761640321-b80251be00ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const STATUS_CLS = {
  active: "bg-violet/10 text-violet",
  draft: "bg-gold/15 text-gold",
  archived: "bg-navy/10 text-navy/50",
};

export default function AuraCommerce() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const canManage = ["admin", "ceo", "director"].includes(user?.role);
  const navigate = useNavigate();
  const tf = useTeamFilter();
  const [metrics, setMetrics] = useState(null);
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [pOpen, setPOpen] = useState(false);
  const [cOpen, setCOpen] = useState(false);
  const [pres, setPres] = useState([]);
  const [prOpen, setPrOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/aura/overview").then((r) => setMetrics(r.data.metrics)).catch(() => {});
    api.get(`/aura/products${filter ? `?status=${filter}` : ""}`).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/aura/collections").then((r) => setCollections(r.data)).catch(() => {});
    api.get("/aura/presentations").then((r) => setPres(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const money = (p) => `$${Number(p).toLocaleString()}`;

  const [share, setShare] = useState(null);
  const delItem = async (kind, id, name) => {
    if (!window.confirm(`¿Eliminar "${name}"? Esta acción no se puede deshacer.`)) return;
    try { await api.delete(`/aura/${kind}/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const delPres = async (p) => {
    if (!window.confirm(`¿Eliminar la presentación "${p.title}"? Dejará de verse en la página pública (recuperable desde la Papelera).`)) return;
    try { await api.delete(`/aura/presentations/${p.id}`); toast.success("Presentación eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  const movePres = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= pres.length) return;
    try {
      await api.patch(`/aura/presentations/${pres[idx].id}`, { order: j + 1 });
      await api.patch(`/aura/presentations/${pres[j].id}`, { order: idx + 1 });
      load();
    } catch { toast.error("No se pudo reordenar"); }
  };

  const presLink = (p) => `${window.location.origin}/marca/aura?presentacion=${p.id}`;
  const copyPresLink = async (p) => {
    try { await navigator.clipboard.writeText(presLink(p)); toast.success("Enlace copiado — compártelo por WhatsApp o correo"); }
    catch { toast.error("No se pudo copiar el enlace"); }
  };

  const shown = products.filter((p) => {
    const q = query.trim().toLowerCase();
    return !q || p.name?.toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q);
  });

  return (
    <OSLayout title={t("os.nav.aura")}>
      {!metrics ? (
        <div className="text-navy/40">{t("os.loading")}</div>
      ) : (
        <div className="space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
              <div className="overline text-navy/40 mb-2">AURA · Living Intelligence</div>
              <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Commerce</h2>
              <p className="mt-2 text-navy/50">{t("os.aura.subtitle")}</p>
            </motion.div>
            <div className="flex gap-2">
              <div className="self-center mr-1"><OwnerFilter tf={tf} /></div>
              <button data-testid="aura-new-collection" onClick={() => setCOpen(true)}
                className="inline-flex items-center gap-2 rounded-full border border-navy/15 text-sm px-4 py-3 hover:border-violet hover:text-violet transition-colors">
                <Plus className="w-4 h-4" /> {t("os.aura.newCollection")}
              </button>
              {canManage && (
                <button data-testid="aura-new-product" onClick={() => setPOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity">
                  <Plus className="w-4 h-4" /> {t("os.aura.newProduct")}
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            {metrics.map((m, i) => (
              <motion.div key={m.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: i * 0.05 }} data-testid={`aura-kpi-${m.key}`}
                className="bg-white border border-navy/10 rounded-lg p-5">
                <div className="overline text-navy/40 text-[0.6rem]">{lang === "en" ? m.label_en : m.label_es}</div>
                <div className="mt-2 text-2xl font-medium tracking-tight">{m.value}</div>
              </motion.div>
            ))}
          </div>

          {/* Collections strip */}
          {collections.length > 0 && (
            <div>
              <div className="overline text-navy/40 mb-3">{t("os.aura.collections")}</div>
              <div className="flex gap-4 overflow-x-auto thin-scroll pb-2">
                {tf.filter(collections).map((c) => (
                  <div key={c.id} data-testid={`aura-collection-${c.id}`} className="shrink-0 w-64 bg-white border border-navy/10 rounded-lg overflow-hidden">
                    <div className="aspect-[16/9] bg-cream overflow-hidden"><img src={mediaUrl(c.cover) || FALLBACK_IMG} alt={c.name} className="w-full h-full object-cover" /></div>
                    <div className="p-4 flex items-start justify-between">
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-navy/40 mt-0.5">{c.product_count} · {t("os.aura.products")}</div>
                      </div>
                      <ItemMenu testId={c.id} onInvite={() => setShare({ collection: "aura_collections", id: c.id, name: c.name })}
                        onDelete={() => delItem("collections", c.id, c.name)} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Presentaciones públicas (PDF) — se muestran en /marca/aura */}
          <div data-testid="aura-presentations-panel">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div>
                <div className="overline text-navy/40">Presentaciones / Catálogos · /marca/aura</div>
                <p className="text-xs text-navy/40 mt-1">Los PDF que subas aquí se muestran a cualquier visitante en la página pública de AURA.</p>
              </div>
              <button data-testid="aura-new-presentation" onClick={() => setPrOpen(true)}
                className="inline-flex items-center gap-2 rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-violet hover:text-violet transition-colors">
                <Plus className="w-4 h-4" /> Subir presentación
              </button>
            </div>
            {pres.length === 0 ? (
              <div className="text-sm text-navy/40 border border-dashed border-navy/15 rounded-lg py-6 text-center">
                Aún no hay presentaciones. Sube un PDF para publicarlo en la página de AURA.
              </div>
            ) : (
              <div className="bg-white border border-navy/10 rounded-lg divide-y divide-navy/5">
                {pres.map((p, idx) => (
                  <div key={p.id} data-testid={`aura-pres-row-${p.id}`} className="flex items-center gap-3 px-4 py-3">
                    {p.cover
                      ? <img src={mediaUrl(p.cover)} alt="" className="w-12 h-8 object-cover object-top rounded border border-navy/10 shrink-0" />
                      : <FileText className="w-4 h-4 text-violet shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{p.title}</div>
                      <div className="text-[11px] text-navy/40">{(p.size / 1048576).toFixed(1)} MB · {String(p.created_at).slice(0, 10)}{p.owner_name ? ` · ${p.owner_name}` : ""}</div>
                    </div>
                    <button onClick={() => movePres(idx, -1)} disabled={idx === 0} data-testid={`aura-pres-up-${p.id}`}
                      className="text-navy/30 hover:text-navy disabled:opacity-20"><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => movePres(idx, 1)} disabled={idx === pres.length - 1} data-testid={`aura-pres-down-${p.id}`}
                      className="text-navy/30 hover:text-navy disabled:opacity-20"><ChevronDown className="w-4 h-4" /></button>
                    <button onClick={() => copyPresLink(p)} data-testid={`aura-pres-copy-${p.id}`}
                      className="text-navy/40 hover:text-violet" title="Copiar enlace público"><Link2 className="w-4 h-4" /></button>
                    <a href={`https://wa.me/?text=${encodeURIComponent(`${p.title} — ${presLink(p)}`)}`} target="_blank" rel="noreferrer"
                      data-testid={`aura-pres-wa-${p.id}`} className="text-navy/40 hover:text-[#25D366]" title="Compartir por WhatsApp">
                      <WhatsAppIcon className="w-4 h-4" /></a>
                    <a href={mediaUrl(p.url)} target="_blank" rel="noreferrer" data-testid={`aura-pres-view-${p.id}`}
                      className="text-navy/40 hover:text-violet" title="Ver PDF"><ExternalLink className="w-4 h-4" /></a>
                    <button onClick={() => delPres(p)} data-testid={`aura-pres-del-${p.id}`}
                      className="text-navy/30 hover:text-destructive" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative w-full md:max-w-xs">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/30" />
              <input data-testid="aura-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("shop.search")}
                className="w-full bg-white border border-navy/10 rounded-full pl-11 pr-4 py-2.5 outline-none focus:border-violet transition-colors text-sm" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[["", "all"], ["active", "active"], ["draft", "draft"], ["archived", "archived"]].map(([val, key]) => (
                <button key={key} data-testid={`aura-filter-${key}`} onClick={() => setFilter(val)}
                  className={`text-sm px-4 py-2 rounded-full transition-colors ${filter === val ? "bg-navy text-white" : "border border-navy/15 text-navy/60 hover:border-navy/40"}`}>
                  {t(`os.aura.filters.${key}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Products grid */}
          {shown.length === 0 ? (
            <div className="text-navy/40 py-16 text-center border border-dashed border-navy/15 rounded-lg">{t("os.aura.empty")}</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {tf.filter(shown).map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: (i % 4) * 0.05 }} className="relative">
                  <div className="absolute top-3 right-3 z-10 bg-white/90 rounded-md">
                    <ItemMenu testId={p.id} onInvite={() => setShare({ collection: "aura_products", id: p.id, name: p.name })}
                      onEdit={() => navigate(`/os/aura/${p.id}`)} onDelete={() => delItem("products", p.id, p.name)} />
                  </div>
                  <Link to={`/os/aura/${p.id}`} data-testid={`aura-product-${p.id}`}
                    className="group block bg-white border border-navy/10 rounded-lg overflow-hidden hover:-translate-y-1 hover:border-navy/25 transition-all duration-500">
                    <div className="aspect-square bg-cream overflow-hidden relative">
                      <img src={mediaUrl(p.cover) || FALLBACK_IMG} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <span className={`absolute top-3 left-3 text-[10px] px-2 py-1 rounded-full ${STATUS_CLS[p.status]}`}>{t(`os.aura.status.${p.status}`)}</span>
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{p.name}</div>
                          <div className="text-xs text-navy/40 truncate">{p.collection || p.category}</div>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-navy/30 group-hover:text-violet transition-colors shrink-0" />
                      </div>
                      <div className="mt-3 text-lg font-medium">{money(p.price)}</div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {pOpen && <ProductModal collections={collections} onClose={() => setPOpen(false)}
        onCreated={(p) => navigate(`/os/aura/${p.id}`)} />}
      {prOpen && <PresModal onClose={() => setPrOpen(false)} onCreated={() => { setPrOpen(false); load(); }} />}
      {cOpen && <CollectionModal onClose={() => setCOpen(false)} onCreated={() => { setCOpen(false); load(); }} />}
      {share && <ShareDialog collection={share.collection} id={share.id} title={share.name} onClose={() => setShare(null)} />}
    </OSLayout>
  );
}

function ProductModal({ collections, onClose, onCreated }) {
  const { t } = useI18n();
  const [f, setF] = useState({ name: "", sku: "", price: 0, category: "Objetos", collection: "", weight: "", length: "", width: "", height: "" });
  const [err, setErr] = useState("");
  const create = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const { data } = await api.post("/aura/products", {
        ...f, price: Number(f.price) || 0,
        weight: Number(f.weight) || 0, length: Number(f.length) || 0, width: Number(f.width) || 0, height: Number(f.height) || 0,
      });
      toast.success(t("os.aura.create.submit")); onCreated(data);
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
  };
  return (
    <Modal title={t("os.aura.create.title")} onClose={onClose} testid="aura-product-modal">
      <form onSubmit={create} className="space-y-4">
        <input required data-testid="aura-input-name" placeholder={t("os.aura.create.name")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
        <input data-testid="aura-input-sku" placeholder={t("os.aura.create.sku")} value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} className={inputCls} />
        <div className="grid grid-cols-2 gap-3">
          <input type="number" data-testid="aura-input-price" placeholder={t("os.aura.create.price")} value={f.price || ""} onChange={(e) => setF({ ...f, price: e.target.value })} className={inputCls} />
          <select data-testid="aura-input-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inputCls}>
            <option>Objetos</option><option>Decoración</option><option>Iluminación</option><option>Textil</option><option>Mobiliario</option>
          </select>
        </div>
        <select data-testid="aura-input-collection" value={f.collection} onChange={(e) => setF({ ...f, collection: e.target.value })} className={inputCls}>
          <option value="">{t("os.aura.create.collection")}</option>
          {collections.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <div className="pt-1">
          <div className="text-xs text-navy/50 mb-1.5">Envío — peso y dimensiones (para cotizar automáticamente en la tienda)</div>
          <div className="grid grid-cols-4 gap-2">
            <input type="number" step="0.1" data-testid="aura-input-weight" placeholder="Peso kg" value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} className={inputCls} />
            <input type="number" step="1" data-testid="aura-input-length" placeholder="Largo cm" value={f.length} onChange={(e) => setF({ ...f, length: e.target.value })} className={inputCls} />
            <input type="number" step="1" data-testid="aura-input-width" placeholder="Ancho cm" value={f.width} onChange={(e) => setF({ ...f, width: e.target.value })} className={inputCls} />
            <input type="number" step="1" data-testid="aura-input-height" placeholder="Alto cm" value={f.height} onChange={(e) => setF({ ...f, height: e.target.value })} className={inputCls} />
          </div>
        </div>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <button data-testid="aura-product-submit" className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">{t("os.aura.create.submit")}</button>
      </form>
    </Modal>
  );
}

function CollectionModal({ onClose, onCreated }) {
  const { t } = useI18n();
  const [f, setF] = useState({ name: "", description: "" });
  const create = async (e) => {
    e.preventDefault();
    await api.post("/aura/collections", { ...f, cover: "https://images.unsplash.com/photo-1677761640321-b80251be00ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" });
    toast.success(t("os.aura.createCollection.submit")); onCreated();
  };
  return (
    <Modal title={t("os.aura.createCollection.title")} onClose={onClose} testid="aura-collection-modal">
      <form onSubmit={create} className="space-y-4">
        <input required data-testid="aura-coll-name" placeholder={t("os.aura.createCollection.name")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
        <textarea data-testid="aura-coll-desc" rows={3} placeholder={t("os.aura.createCollection.description")} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={`${inputCls} resize-none`} />
        <button data-testid="aura-collection-submit" className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">{t("os.aura.createCollection.submit")}</button>
      </form>
    </Modal>
  );
}

function PresModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(0);
  const [phase, setPhase] = useState("");
  const CHUNK = 4 * 1024 * 1024;
  const MAX = 400 * 1024 * 1024;

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Selecciona un archivo PDF"); return; }
    if (file.size > MAX) { toast.error("El PDF supera el límite de 400 MB"); return; }
    setBusy(true);
    setProg(0);
    try {
      const uid = (window.crypto?.randomUUID ? window.crypto.randomUUID() : `up${Date.now()}${Math.floor(Math.random() * 1e6)}`);
      const totalChunks = Math.ceil(file.size / CHUNK);
      setPhase("upload");
      for (let i = 0; i < totalChunks; i++) {
        const fd = new FormData();
        fd.append("upload_id", uid);
        fd.append("index", i);
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk.bin");
        await api.post("/aura/presentations/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProg(Math.round(((i + 1) / totalChunks) * 100));
      }
      setPhase("process");
      await api.post("/aura/presentations/finalize", {
        upload_id: uid, title: title || file.name.replace(/\.pdf$/i, ""), filename: file.name,
      });
      for (let t = 0; t < 240; t++) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data } = await api.get(`/aura/presentations/upload-status/${uid}`);
        if (data.status === "done") {
          toast.success("Presentación publicada en /marca/aura");
          onCreated();
          return;
        }
        if (data.status === "error") throw new Error(data.error || "Error al procesar el PDF");
      }
      throw new Error("Tiempo de espera agotado al procesar");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message || "Error al subir el PDF");
      setBusy(false);
      setPhase("");
      setProg(0);
    }
  };

  return (
    <Modal title="Subir presentación (PDF)" onClose={busy ? () => {} : onClose} testid="aura-pres-modal">
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="aura-pres-title" placeholder="Título de la presentación" value={title}
          onChange={(e) => setTitle(e.target.value)} disabled={busy} className={inputCls} />
        <label className="block border border-dashed border-navy/20 rounded-lg p-5 text-center cursor-pointer hover:border-violet transition-colors">
          <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="aura-pres-file" disabled={busy}
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <FileText className="w-6 h-6 mx-auto text-navy/30 mb-2" />
          <div className="text-sm">{file ? file.name : "Haz clic para elegir el PDF (máx. 400 MB)"}</div>
          {file && <div className="text-xs text-navy/40 mt-1">{(file.size / 1048576).toFixed(1)} MB</div>}
        </label>
        {busy && (
          <div data-testid="aura-pres-progress">
            <div className="h-2 rounded-full bg-navy/10 overflow-hidden">
              <div className="h-full bg-gradient-violet transition-all duration-300" style={{ width: `${phase === "process" ? 100 : prog}%` }} />
            </div>
            <div className="text-xs text-navy/50 mt-1.5">
              {phase === "upload" ? `Subiendo… ${prog}%` : "Procesando y generando portada…"}
            </div>
          </div>
        )}
        <p className="text-xs text-navy/40">Al publicarla, cualquier visitante podrá verla en omniagroup.mx/marca/aura.</p>
        <button disabled={busy} data-testid="aura-pres-submit"
          className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {phase === "process" ? "Procesando…" : "Subiendo…"}</> : "Publicar presentación"}
        </button>
      </form>
    </Modal>
  );
}

const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors";

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.4, ease }}
        onClick={(e) => e.stopPropagation()} data-testid={testid} className="w-full max-w-md bg-cream rounded-xl p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-medium">{title}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}
